// 收件箱主页 —— tabs + 高级筛选 + 表格
// - 同一组件兼任文件夹页（/app/folders/:folderId，见 FolderPage）：folderId 缺省 = 收件箱，
//   只列未进任何文件夹的邮件；有 folderId 则只列该文件夹
// - Tabs：全部 / 未读 / 已读 / 已转发
// - 高级筛选：展开后可填 发件域 / 有附件 / 日期范围；与 tabs 联合生效
// - 数据展示委托给 MessageList

import { Filter, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AliasCombobox } from '../components/AliasCombobox.js';
import { MessageList } from '../components/MessageList.js';
import { type InboxTab, type MessagesFilter } from '../lib/messages.js';

interface TabDef {
  key: InboxTab;
  label: string;
}

const TABS: TabDef[] = [
  { key: 'all', label: '全部' },
  { key: 'unread', label: '未读' },
  { key: 'read', label: '已读' },
  { key: 'forwarded', label: '已转发' },
];

const ATTACHMENT_OPTIONS: { key: AttachmentOpt; label: string }[] = [
  { key: 'all', label: '全部邮件' },
  { key: 'has', label: '仅带附件' },
  { key: 'none', label: '仅无附件' },
];

type AttachmentOpt = 'all' | 'has' | 'none';

const FILTER_PARAM_KEYS = ['alias', 'from', 'att', 'start', 'end'] as const;

function parseTab(v: string | null): InboxTab {
  return TABS.some((t) => t.key === v) ? (v as InboxTab) : 'all';
}

/** URL → 已应用筛选；日期保留 YYYY-MM-DD 原样，既回填 date input 又换算时间戳。 */
function parseApplied(p: URLSearchParams) {
  const alias = Number(p.get('alias'));
  const att = p.get('att');
  const day = (k: string) => {
    const v = p.get(k) ?? '';
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
  };
  return {
    aliasId: Number.isInteger(alias) && alias > 0 ? alias : null,
    fromDomain: p.get('from')?.trim() ?? '',
    attachment: att === 'has' || att === 'none' ? att : 'all',
    dateStart: day('start'),
    dateEnd: day('end'),
  };
}

/** YYYY-MM-DD + 当天起/止时刻 → { [field]: unix 秒 }；空或非法返回 {}。 */
function dayBound(
  field: 'dateStart' | 'dateEnd',
  day: string,
  time: string,
): Partial<MessagesFilter> {
  if (!day) return {};
  const t = Math.floor(new Date(day + time).getTime() / 1000);
  return Number.isFinite(t) ? { [field]: t } : {};
}

function setOrDelete(p: URLSearchParams, key: string, value: string): void {
  if (value) p.set(key, value);
  else p.delete(key);
}

interface InboxPageProps {
  /** 文件夹页传入；缺省 = 收件箱 */
  folderId?: number;
  title?: string;
  subtitle?: string;
  /** 页头右侧额外操作（文件夹页的改名/删除） */
  headerActions?: ReactNode;
}

export function InboxPage({
  folderId,
  title = '收件箱',
  subtitle = '管理所有收到的邮件（已移入文件夹的除外）',
  headerActions,
}: InboxPageProps = {}) {
  // 标签与已应用的筛选放 URL（?tab=unread&from=…），不放组件 state：
  // 点进详情会卸载本页，详情页「返回」走 navigate(-1)，URL 在就能原样恢复。
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get('tab'));
  const applied = parseApplied(params);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  // 输入态：从 URL 初始化，点"筛选"才 commit 回 URL（避免每键都打后端）
  const [aliasId, setAliasId] = useState<number | null>(applied.aliasId);
  const [fromDomain, setFromDomain] = useState(applied.fromDomain);
  const [attachmentOpt, setAttachmentOpt] = useState(applied.attachment);
  const [dateStart, setDateStart] = useState(applied.dateStart);
  const [dateEnd, setDateEnd] = useState(applied.dateEnd);

  const activeFilterCount = [
    applied.aliasId != null,
    applied.fromDomain !== '',
    applied.attachment !== 'all',
    applied.dateStart !== '',
    applied.dateEnd !== '',
  ].filter(Boolean).length;
  const hasActiveFilter = activeFilterCount > 0;

  const filter: MessagesFilter = {
    tab,
    folder: folderId ?? 'inbox',
    ...(applied.aliasId != null ? { aliasId: applied.aliasId } : {}),
    ...(applied.fromDomain ? { fromDomain: applied.fromDomain } : {}),
    ...(applied.attachment !== 'all' ? { hasAttachment: applied.attachment === 'has' } : {}),
    ...dayBound('dateStart', applied.dateStart, 'T00:00:00'),
    ...dayBound('dateEnd', applied.dateEnd, 'T23:59:59'),
  };

  // replace：切标签 / 筛选不堆历史，否则详情页「返回」要按好几次才回到列表。
  const updateParams = (edit: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    edit(next);
    setParams(next, { replace: true });
  };

  const setTab = (t: InboxTab) =>
    updateParams((p) => {
      if (t === 'all') p.delete('tab');
      else p.set('tab', t);
    });

  const applyFilter = () => {
    updateParams((p) => {
      setOrDelete(p, 'alias', aliasId != null ? String(aliasId) : '');
      setOrDelete(p, 'from', fromDomain.trim());
      setOrDelete(p, 'att', attachmentOpt === 'all' ? '' : attachmentOpt);
      setOrDelete(p, 'start', dateStart);
      setOrDelete(p, 'end', dateEnd);
    });
    setAdvancedOpen(false);
  };

  const resetFilter = () => {
    updateParams((p) => {
      for (const k of FILTER_PARAM_KEYS) p.delete(k);
    });
    setAliasId(null);
    setFromDomain('');
    setAttachmentOpt('all');
    setDateStart('');
    setDateEnd('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold whitespace-nowrap">{title}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerActions}
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className={
              'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-medium transition-colors whitespace-nowrap ' +
              (hasActiveFilter
                ? 'border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800')
            }
          >
            <Filter className="h-3.5 w-3.5" />
            高级筛选
            {hasActiveFilter ? `（${String(activeFilterCount)}）` : ''}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap gap-1 border-b border-slate-100 px-3 py-2 dark:border-slate-800">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={
                'inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ' +
                (tab === t.key
                  ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-200'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800')
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {advancedOpen ? (
          <div className="border-b border-slate-100 p-3 dark:border-slate-800">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  别名
                </label>
                <AliasCombobox value={aliasId} onChange={setAliasId} />
              </div>
              {/* 窄屏让发件域名独占一行（外层 div 也要 w-full，否则 input 的 w-full 无参照） */}
              <div className="w-full sm:w-auto">
                <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  发件域名
                </label>
                <input
                  type="text"
                  value={fromDomain}
                  onChange={(e) => setFromDomain(e.target.value)}
                  placeholder="github.com"
                  className="h-9 w-full rounded-lg border sm:w-44 border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  附件
                </label>
                <select
                  value={attachmentOpt}
                  onChange={(e) => setAttachmentOpt(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950 whitespace-nowrap"
                >
                  {ATTACHMENT_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  起始日期
                </label>
                <input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950"
                />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  截止日期
                </label>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950"
                />
              </div>
              <div className="ml-auto flex gap-2">
                <button
                  type="button"
                  onClick={resetFilter}
                  className="inline-flex h-9 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 whitespace-nowrap"
                >
                  <X className="h-3.5 w-3.5" />
                  清除
                </button>
                <button
                  type="button"
                  onClick={applyFilter}
                  className="inline-flex h-9 items-center rounded-lg bg-brand-500 px-4 text-xs font-medium text-white hover:bg-brand-600 whitespace-nowrap"
                >
                  筛选
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <MessageList filter={filter} />
      </div>
    </div>
  );
}
